// Where everything sits on the stage, in one place.
//
// The shield is a MAP — fifteen figures at forty pixels, showing the shape of
// the whole working. It is the wrong surface to animate on: a dot arriving
// there moves about ten pixels inside a grid of fifteen other figures, and
// nobody sees it. The first attempt animated the map and passed every test,
// because the tests asked whether classes changed rather than whether anything
// was visible.
//
// So the move being made is drawn AGAIN, large, on its own stage, and that is
// where the motion happens. Same rule as the Tree's label geometry: the
// positions live in one module, so the renderer and anything measuring it
// cannot drift apart.
export const FIGURE_WIDTH = 90;
export const LINE_GAP = 30;
export const TOP = 38;
export const DOT_R = 7;
export const PAIR_DX = 20;
export const GAP = 26;
export const OPERATOR = 62;
// Room for two label rows UNDER the dots. At 34 the printed name sat on top of
// the fourth line of the figure — the bottom dot ends at TOP+3*GAP+DOT_R and
// the baseline was eighteen units above that.
export const HEIGHT = TOP + LINE_GAP * 3 + DOT_R + 52;

export const lineY = (line) => TOP + line * LINE_GAP;

// A line of one dot sits in the middle; a pair sits either side of it.
export function dotXs(dots, originX) {
  const middle = originX + FIGURE_WIDTH / 2;
  return dots === 2 ? [middle - PAIR_DX, middle + PAIR_DX] : [middle];
}

// The figures on the stage, left to right, each with the x it starts at.
// LEFT TO RIGHT AND LABELLED, deliberately: the shield reads right to left
// because the points are cast that way, but a diagram whose job is to show one
// operation should read the way an equation does. Every figure carries its
// name, so there is nothing to infer from the order.
export function layout(step, { compact = false, line = null } = {}) {
  if (!step) return { slots: [], width: FIGURE_WIDTH, height: HEIGHT };

  if (step.kind === "cast") {
    const slots = step.made.map((slug, index) => ({
      key: `cast-${index}`,
      slug,
      label: `Mother ${index + 1}`,
      short: `Mother ${index + 1}`,
      x: index * (FIGURE_WIDTH + GAP),
      role: "made",
      firing: false
    }));
    return { slots, width: slots.length * (FIGURE_WIDTH + GAP) - GAP, height: HEIGHT };
  }

  // WHICH SOURCES ARE IN PLAY, and it is not always all of them. An addition
  // consumes the same row of both figures, so both fire. A TRANSPOSITION does
  // not: Daughter 3's first row is Mother 1's third line and nothing else, so
  // exactly one Mother fires per beat and the other three are bystanders.
  const firingIndex = step.kind === "transpose" ? line : null;
  const isFiring = (index) => (step.kind === "transpose" ? index === firingIndex : true);

  // ON A NARROW SCREEN, THE BYSTANDERS GO. Four Mothers, an arrow and a
  // Daughter is six hundred units of stage; at 375px that renders each figure
  // back down to the forty pixels the shield already gives them, which is the
  // exact failure this stage exists to fix. So a phone sees the Mother being
  // read and the figure being built — and the other three are on the shield
  // immediately below, and named in the rows under the stage.
  const wanted =
    compact && step.kind === "transpose"
      ? step.sources
          .map((source, index) => ({ source, index }))
          .filter(({ index }) => index === (firingIndex ?? step.sources.length - 1))
      : step.sources.map((source, index) => ({ source, index }));

  const spacing = FIGURE_WIDTH + (step.kind === "add" ? OPERATOR : GAP);
  const sources = wanted.map(({ source, index }, position) => ({
    key: `source-${index}`,
    slug: source.figure,
    label: source.note,
    // The stage gives a figure 90 units of width and "Mother 1, third line" is
    // three times that. The band already says WHICH line is being read.
    short: source.note.split(",")[0],
    line: source.line,
    sourceIndex: index,
    x: position * spacing,
    role: "source",
    firing: isFiring(index)
  }));

  const last = sources[sources.length - 1];
  const resultX = last.x + FIGURE_WIDTH + OPERATOR;
  const slots = [
    ...sources,
    { key: "result", slug: step.made[0], label: "makes", short: "makes", x: resultX, role: "made", firing: false }
  ];

  return { slots, width: resultX + FIGURE_WIDTH, height: HEIGHT };
}

// The dots that travel for one line of one move: where each starts, and how
// far it goes. A transposition moves a line intact, so its dots travel one for
// one; an addition consumes two lines to make one, so both sources' dots
// travel to where the result will be and the result appears as they land.
export function travellers({ step, layoutResult, figures, line }) {
  if (!step || step.kind === "cast" || line === null) return [];

  const slots = layoutResult.slots;
  const result = slots[slots.length - 1];
  const made = figures[result.slug];
  const targetY = lineY(line);

  return slots
    .filter((slot) => slot.role === "source" && slot.firing)
    .flatMap((slot, index) => {
      const figure = figures[slot.slug];
      // A transposition reads the line its source NAMES, which is not the row
      // it lands on — Daughter 3 takes every Mother's third line.
      const readLine = step.kind === "transpose" ? slot.line : line;
      if (readLine === null || readLine === undefined) return [];

      const dots = figure.lines[readLine];
      const fromY = lineY(readLine);
      const targets = dotXs(made.lines[line], result.x);

      return dotXs(dots, slot.x).map((x, dot) => ({
        key: `${slot.key}-${dot}`,
        x,
        y: fromY,
        dx: (targets[Math.min(dot, targets.length - 1)] ?? targets[0]) - x,
        dy: targetY - fromY,
        source: index
      }));
    });
}

// Which line of a slot is being read right now, or null if it is a bystander.
export function readingNow(slot, step, line) {
  if (!slot || !step || line === null || step.kind === "cast") return null;
  if (slot.role !== "source" || !slot.firing) return null;
  return step.kind === "transpose" ? slot.line : line;
}
